/**
 * ChatbotWidget - 유가보조금 포털 챗봇
 *
 * [전체 아키텍처]
 * 프론트(ChatbotWidget)
 *   → Next.js API Route (/api/chatbot/stream)
 *       → 연구소 챗봇 서버(211.43.198.100:8091) : SSE 스트리밍 답변
 *
 * [API 목록]
 *   POST /api/chatbot/stream   : SSE 스트리밍 질문/답변 (기본)
 *   POST /api/chatbot/message  : 일반(비스트리밍) 질문/답변
 *   POST /api/chatbot/feedback : 답변 만족도 (좋아요/싫어요) - TODO
 *   GET  /api/chatbot/suggestions : 추천 질문 - TODO
 *
 * [세션]
 *   로그인 없이 localStorage UUID로 익명 세션 관리
 *   sessionId는 done 이벤트로 서버에서 발급된 값으로 갱신됨
 *
 * [이력 저장 - 통계 연동]
 *   통계는 qstn_cn 의 '**코드' 접두어로 정상/제외/오류를 구분한다.
 *   422 비속어 / 423 유가보조금 무관 / 424 학습데이터 없음 / 5xx 연구소 응답 에러
 *
 * 2026.09.01 / 김혜령 / 423 마커 저장 복구, 424 케이스 추가
 */
'use client';
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Fab,
  Paper,
  Typography,
  TextField,
  IconButton,
  Slide,
  Avatar,
  Stack,
  Divider,
  CircularProgress,
  Collapse,
  Switch,
} from '@mui/material'
import { IconX, IconSend, IconStar, IconStarFilled, IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import Chip from '@mui/material/Chip';
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm';
import RobotIcon from './RobotIcon';
import { useAuth } from '@/common/_hooks/AuthContext';
import { useRequestApi } from '@/common/_hooks/useRequestApi'
import { getErrorCatch } from '@/common/_utils/errorCatch'
import { useMessageActions } from '@/common/_hooks/MessageContext'
import { useCode } from '@/common/_hooks/CodeContext'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import chatbotIcon16 from './images/16_16.png'
import chatbotIcon24 from './images/24_24.png'
import chatbotIcon32 from './images/32_32.png'
import chatbotIcon64 from './images/64_64_info.png'
import chatbotIconButton33 from './images/33_46_button_name.png'

interface Citation {
  title: string;
  pageNumber: number;
  text: string;
}

interface FeedbackState {
  rating: number;
  tag: string | null;
  submitted: boolean;
}

const TAGS_BY_RATING: {
  high: { cdNm: string; cdKornNm: string }[];
  middle: { cdNm: string; cdKornNm: string }[];
  low: { cdNm: string; cdKornNm: string }[];
} = {
  high: [],
  middle: [],
  low: [],
}

const FeedbackPanel = ({
  hstryTsid,
  onSubmit,
  current,
}: {
  hstryTsid?: string;
  onSubmit: (hstryTsid: string, feedback: { rating: number; tag: string | null}) => void;
  current?: FeedbackState;
}) => {
  const [rating, setRating] = useState(current?.rating ?? 0)
  const [hovered, setHovered] = useState(0)
  const [selectedTag, setSelectedTag] = useState<string | null>(null)

  
  const displayRating = hovered || rating;
  const tags = rating >= 4 ? TAGS_BY_RATING.high : rating === 3 ? TAGS_BY_RATING.middle : rating > 0 ? TAGS_BY_RATING.low : [];
  
  const toggleTag = (cdNm: string) => {
    setSelectedTag(prev =>
      prev === cdNm ? null : cdNm
    )
  }
  
  useEffect(() => {    
    setSelectedTag(null)
  }, [rating])
  
  if (current?.submitted) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5, display: 'block', mt: 0.5 }}>
        피드백 감사합니다 🙏
      </Typography>
    );
  }

  return (
    <Box sx={{ pl: 0.5, mt: 0.5 }}>
      {/* 별점 */}
      <Stack direction="row" spacing={0.2} alignItems="center">
        {[1, 2, 3, 4, 5].map((i) => (
          <IconButton
            key={i}
            size="small"
            sx={{ p: 0.3, color: i <= displayRating ? '#facc15' : 'grey.300' }}
            onMouseEnter={() => setHovered(i)}
            onMouseLeave={() => setHovered(0)}
            onClick={() => { setRating(i)}}
          >
            {i <= displayRating ? <IconStarFilled size={16} /> : <IconStar size={16} />}
          </IconButton>
        ))}
      </Stack>

      {/* 선택 태그 */}
      {rating > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.8 }}>
          {tags.map((tag) => (
            <Chip
              key={tag.cdNm}
              label={tag.cdKornNm}
              size="small"
              variant={selectedTag ===tag.cdNm ? 'filled' : 'outlined'}
              color={selectedTag === tag.cdNm ? 'primary' : 'default'}
              onClick={() => toggleTag(tag.cdNm)}
              sx={{ fontSize: '0.65rem', height: 22 }}
            />
          ))}
        </Box>
      )}

      {/* 제출 버튼 */}
      {rating > 0 && (
        <Box sx={{ mt: 0.8 }}>
          <Chip
            label="제출"
            size="small"
            color="primary"
            clickable
              onClick={() => {
                if (!hstryTsid) return

                onSubmit(hstryTsid, {
                  rating,
                  tag: selectedTag,
                })
              }}
            sx={{ fontSize: '0.65rem', height: 22 }}
          />
        </Box>
      )}
    </Box>
  );
};

interface Message {
  id: string;
  role: 'user' | 'bot';
  content: string;
  hstryTsid?: string;
  citations?: Citation[];
  streaming?: boolean;
  error?: boolean; // 가드레일/에러 안내 메시지 → 만족도 평가 미노출
}

const generateUUID = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
};

const getOrCreateSessionId = (): string => {
  const key = 'chatbot_session_id';
  let sid = localStorage.getItem(key);
  if (!sid) {
    sid = generateUUID();
    localStorage.setItem(key, sid);
  }
  return sid;
};

const removeFileExtension = (filename: string): string => {
  return filename.replace(/\.[^/.]+$/, '');
}

// 출처 목록 토글 컴포넌트
const CitationList = ({ citations }: { citations: Citation[] }) => {
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{ mt: 0.5 }}>
      <Box
        component="button"
        onClick={() => setOpen((v) => !v)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: 'text.secondary',
          fontSize: '0.7rem',
          px: 0,
          py: 0,
        }}
      >
        {open ? <IconChevronUp size={12} /> : <IconChevronDown size={12} />}
        출처 {citations.length}건
      </Box>
      <Collapse in={open}>
        <Stack spacing={0.5} sx={{ mt: 0.5 }}>
          {citations.map((c, i) => (
            <Box
              key={i}
              sx={{
                px: 1,
                py: 0.5,
                bgcolor: 'grey.100',
                borderRadius: 1,
                borderLeft: '3px solid',
                borderColor: 'primary.light',
              }}
            >
              <Typography variant="caption" fontWeight={600} display="block">
                {removeFileExtension(c.title)} {c.pageNumber ? `(p.${c.pageNumber})` : ''}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
};

// 마크다운 답변 렌더링 (채팅 말풍선 크기에 맞춘 여백/폰트 조정)
const MarkdownMessage = ({ content }: { content: string }) => {

  const markdownContent = content.replace(/\r?\n/g, '  \n')

  return (
      <Box
        sx={{
          fontSize: '0.875rem',
          lineHeight: 1.6,
          wordBreak: 'break-word',
          '& p': { m: 0, mb: 0.5, '&:last-child': { mb: 0 } },
          '& ul, & ol': { m: 0, mb: 0.5, pl: 2.5 },
          '& li': { mb: 0.25 },
          '& h1, & h2, & h3, & h4': { m: 0, mb: 0.5, fontSize: '0.95rem', fontWeight: 700 },
          '& code': {
            bgcolor: 'grey.100',
            px: 0.5,
            borderRadius: 0.5,
            fontSize: '0.8rem',
            fontFamily: 'monospace',
          },
          '& pre': {
            bgcolor: 'grey.100',
            p: 1,
            borderRadius: 1,
            overflowX: 'auto',
            my: 0.5,
            '& code': { bgcolor: 'transparent', p: 0 },
          },
          '& a': { color: 'primary.main', textDecoration: 'underline' },
          '& table': { borderCollapse: 'collapse', my: 0.5, width: '100%' },
          '& th, & td': { border: '1px solid', borderColor: 'divider', px: 0.8, py: 0.4, fontSize: '0.8rem' },
          '& blockquote': {
            m: 0,
            my: 0.5,
            pl: 1,
            borderLeft: '3px solid',
            borderColor: 'primary.light',
            color: 'text.secondary',
          },
          '& strong': { fontWeight: 700 },
          '& hr': { my: 1, border: 'none', borderTop: '1px solid', borderColor: 'divider' },
        }}
      >
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdownContent}</ReactMarkdown>
      </Box>
  )
}

export function getApiUri(uri: string) {
    return `/api-be${uri}`
  }

const ChatbotWidget = () => {
  const { auth } = useAuth();
  const lgnId = auth.sttus?.lgnId ?? null;

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [suggestedQuestions, setSuggestedQuestions] = useState<any[]>([])
  const [answerCn, setAnswerCn] = useState('')
  const [errorAnswerTmplt, setErrorAnswerTmplt] = useState<any[]>([])
  const [prhibtPttrn, setPrhibtPttrn] = useState<any[]>([])
  const [feedbackMap, setFeedbackMap] = useState<Record<string, FeedbackState>>({});
  const bottomRef = useRef<HTMLDivElement>(null);
  const { sendHttpRequest } = useRequestApi() // Api(fetch) 호출 함수
  const { setMessage } = useMessageActions()
  const [msgSaveSuccess, setMsgSaveSuccess] = useState(false)
  const [showAll, setShowAll] = useState(false)
  const visibleQuestions = showAll ? suggestedQuestions : suggestedQuestions.slice(0, 4)
  const [messageSave, setMessageSave] = useState<MessageSave>({
    hstryTsid: '',
    ssnId: '',
    qstnCn: '',
    ansCn: '',
    rcmdtnQstnTsid: '',
  });
  const { getCodes } = useCode()
  const chtbotAnsEvlCd = getCodes('ptldb_chtbot_ans_evl_cd').map((c) => ({
    cdKornNm: c.cdKornNm,
    cdNm: c.cdNm,
  }))

  useEffect(() => {
    setSessionId(getOrCreateSessionId());
    // TODO: 선택형 질문 관리 화면에서 등록한 항목(use_yn='Y') 로드
    // fetch('/api/chatbot/suggestions')
    //   .then((res) => res.json())
    //   .then((data) => setSuggestedQuestions(data.questions));
    messageSaveClear()

    fetchAnsEvlCd()

    fetchRcmdtnQstn()
    fetchAnswerTmplt()
    fetchPrhibt()

  }, []);

  //금지단어 패턴 조회
  async function fetchPrhibt() {
    try {

      const responseData = await sendHttpRequest(
        'GET',
        '/chtbot/getByPrhibt',
        null,
        true,
        { cache: 'no-store' },
      )

      if (
        responseData &&
        responseData.resultType === 'success' &&
        responseData.data
      ) {       
          setPrhibtPttrn(responseData.data)          
      }
      
    } catch (error) {
      console.error(error)
    }
  }

  //선택형 질문 조회
  async function fetchRcmdtnQstn() {
    try {

      const responseData = await sendHttpRequest(
        'GET',
        '/chtbot/rcmdtnQstn',
        null,
        true,
        { cache: 'no-store' },
      )

      if (
        responseData &&
        responseData.resultType === 'success' &&
        responseData.data
      ) {         
          setSuggestedQuestions(responseData.data)          
      }
      
    } catch (error) {
      console.error(error)
    }
  }

  //답변 양식 조회
  async function fetchAnswerTmplt() {
    try {

      const endPoint = '/chtbot/getByAnswerTmplt'
      
      const responseData = await sendHttpRequest(
        'POST',
        endPoint,
        null,
        true,
        { cache: 'no-store' },
      )

      if (
        responseData &&
        responseData.resultType === 'success' &&
        responseData.data
      ) 
        {         
          const normalList = responseData.data.filter(
            (item: any) => !item.tmpltNm.includes("**")
          )

          const errorList = responseData.data.filter(
            (item: any) => item.tmpltNm.includes("**")
          )

          setAnswerCn(normalList.map((item: any) => item.cn).join('\n'))
          setErrorAnswerTmplt(errorList)
        } 
    } catch (error) {
      console.error(error)
    }
  }

  //만족도 평가코드 분류
  async function fetchAnsEvlCd() {

    for (const item of chtbotAnsEvlCd) {

      if(item.cdNm.startsWith('CAE1'))
      {
        TAGS_BY_RATING.low.push({
          cdNm: item.cdNm,
          cdKornNm: item.cdKornNm
        })
      }
      if(item.cdNm.startsWith('CAE3'))
      {
        TAGS_BY_RATING.middle.push({
          cdNm: item.cdNm,
          cdKornNm: item.cdKornNm
        })
      }
      if(item.cdNm.startsWith('CAE5'))
      {
        TAGS_BY_RATING.high.push({
          cdNm: item.cdNm,
          cdKornNm: item.cdKornNm
        })
      }
    }
  }

  interface MessageSave
  {
    hstryTsid : string
    rcmdtnQstnTsid?: string
    ssnId : string
    lgnId? : string
    ansEvlCd? : string
    qstnCn : string
    ansCn? : string
    chtbotDgstfnScr? : number
  }

  useEffect(() => {
    
    if (msgSaveSuccess == false && messageSave?.ansCn !== '')
    {
      chtbotHstrySave()
      return    
    }
    if (msgSaveSuccess == true && messageSave.hstryTsid !== '' && (messageSave.chtbotDgstfnScr ?? 0) > 0)
    {
      chtbotHstryUpdate()
      return
    }
  }, [messageSave]) 

  
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, loading])

  function messageSaveClear() {
    setMessageSave({
      hstryTsid: '',
      ssnId: sessionId ?? '',
      qstnCn: '',
      ansCn: '',
    })

    setMsgSaveSuccess(false)
  }

  const chtbotHstryUpdate = async (): Promise<void> => {

    const hstryTsid = messageSave.hstryTsid

    const endpoint = `/chtbot/${hstryTsid}`

      await sendHttpRequest(
          'POST',
          endpoint,
          messageSave,
          true,
          { 'Content-Type': 'application/json' },
          false

        )
    
      messageSaveClear()
  }
  
  const chtbotHstrySave = async (): Promise<void> => {
    const endpoint = '/chtbot'

    try {
      const responseData = await sendHttpRequest(
        'POST',
        endpoint,
        messageSave,
        true,
        { 'Content-Type': 'application/json' },
        false
      )

      if (
        responseData &&
        responseData.resultType === 'success' &&
        responseData.data
      ) {

        setMessageSave(prev => {
          return {
            ...prev,
            hstryTsid: responseData.data.hstryTsid
          }
        })
        setMsgSaveSuccess(true)

        setMessages(prev =>
          prev.map((msg, index) =>
            index === prev.length - 1 && msg.role === 'bot'
              ? { ...msg, hstryTsid: responseData.data.hstryTsid }
              : msg
          )
        )
        
      }
      // Post 제출 끝
    } catch (error) {
      getErrorCatch(error, setMessage)
    }

  }

  const sendFeedback = async (hstryTsid: string, feedback: { rating: number; tag: string | null}) => {
    setFeedbackMap((prev) => ({ ...prev, [hstryTsid]: { ...feedback, submitted: true } }))
    
    setMessageSave(prev => ({
      ...prev,
      chtbotDgstfnScr: feedback.rating,
      ...(feedback.tag && { ansEvlCd: feedback.tag }),
    }))
  }

  const selectQstn = async (qstnTtl: string, rcmdtnQstnTsid: string) => {
    
    setMessages((prev) => [
      ...prev,
      { id: generateUUID(), role: 'user', content: qstnTtl },
    ])

    if (loading) return
    
    setLoading(true)    
    messageSaveClear()

    let endpoint: string =
        `/chtbot/getByRcmdtnQstnTsid?rcmdtnQstnTsid=${rcmdtnQstnTsid}` +
        `&ssnId=${sessionId}` + 
        `&qstnCn=${qstnTtl}`

    const response = await sendHttpRequest(
      'GET',
      endpoint,
      null,
      true,
      {
        cache: 'no-store',
      },
      false,
    )

    setMessageSave(prev => {
      return {
        ...prev,
        ssnId: sessionId,
        qstnCn: qstnTtl,
        ansCn: response.data.qstnCn,
        rcmdtnQstnTsid: rcmdtnQstnTsid
      }
    })

    setMsgSaveSuccess(false)

    setMessages((prev) => [
      ...prev,
      { 
        id: generateUUID(), role: 'bot', content: response.data.qstnCn, streaming: false, error: false
      },
    ])
    setLoading(false)
  }

  const sendMessage = async (text?: string) => {

    const content = (text ?? input).trim()
    if (!content || loading) return
    
    
    const userMsg: Message = { id: generateUUID(), role: 'user', content }
    setMessages((prev) => [...prev, userMsg])
    setInput('')
    setLoading(true)
    messageSaveClear()
    
    const prhibtCheck = prhibtPttrn.find(
      ({ pttrnCn }) => pttrnCn ? new RegExp(pttrnCn).test(content) : false
    )

    if (prhibtCheck) {
      const result = errorAnswerTmplt?.find((item: any) =>
        item.tmpltNm?.includes('**금지단어 패턴 검출 안내'),
      )

      const botMsgId = generateUUID()

      const errorMessage = [
        result?.cn ?? '입력 내용에서 금지 패턴이 검출되었습니다.',
        `검출내용 : ${prhibtCheck.pttrnNm ?? ''}`,
      ].join('\n')

      setLoading(false)
      setMessages((prev) => [
        ...prev,
        {
          id: botMsgId,
          role: 'bot',
          content: errorMessage,
          error: true,
        },
      ])
      setInput('')
      return
    }
    
    const botMsgId = generateUUID();
    let started = false;

    setMessageSave(prev => {
      return {
        ...prev,
        ssnId: sessionId,
        qstnCn: content,
      }
    })

    try {
      const res = await fetch('/api/chatbot/stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: sessionId || null,
          message: content,
          // lgnId: lgnId ?? undefined,  // 로그인 시 포함 - Swagger 확인 후 활성화
        }),
      });

      let answer = ''

      if (!res.ok || !res.body) {
        
        // 가드레일 등 JSON 에러 응답(예: 422 OFF_TOPIC_QUESTION)은
        // 서버가 내려준 안내 메시지를 봇 답변처럼 그대로 표시한다.
        const contentType = res.headers.get('Content-Type') ?? '';
        if (contentType.includes('application/json')) {
          const errData = await res.json().catch(() => null);
          const guideText = errData?.message || '요청을 처리할 수 없습니다.';          

          let errorMsgSave = ''

		  // 통계가 qstn_cn 접두어로 구분하므로 '**코드' 를 반드시 남길 것 (상단 주석 참고)
          switch (res.status) {
            case 422 :
              errorMsgSave = '**422 (비속어) 코드 오류'
              break
            case 423 :
              errorMsgSave = '**423 (관련없음) 코드 오류'
              break
            case 424 :
              errorMsgSave = '**424 (학습데이터 없음) 코드 오류'
              break
            default :   // 502/503 등 5xx = 연구소 응답 에러
              errorMsgSave = '**5XX (연구소 응답 에러) 코드 오류'
              break
          }

          const result = errorAnswerTmplt.find(
            (item: any) => item.tmpltNm.includes(errorMsgSave)  
          )

          const errorMessage = result.cn ? result.cn : guideText

          setMessageSave(prev => {
            return {
              ...prev,
              qstnCn: errorMsgSave,
              ansCn: errorMessage,
            }
          })

          setMessages((prev) => [
            ...prev,
            { id: botMsgId, role: 'bot', content: errorMessage, error: true },
          ]);
          return;
        }
        throw new Error(`서버 오류: ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // \n\n 기준으로 완성된 SSE 이벤트 단위로 분리 (\r\n 도 처리)
        const parts = buffer.split(/\r?\n\r?\n/);
        buffer = parts.pop() ?? '';

        for (const part of parts) {
          if (!part.trim()) continue;

          const lines = part.split(/\r?\n/);
          let eventType = '';
          let dataStr = '';

          for (const line of lines) {
            const trimmed = line.trim();
            if (trimmed.startsWith('event:')) 
            {
              eventType = trimmed.slice(6).trim()             
            }
            else if (trimmed.startsWith('data:')) dataStr = trimmed.slice(5).trim();
          }

          

          if (!eventType || !dataStr) continue;

          try {
            const data = JSON.parse(dataStr);

            if (eventType === 'delta' && data.text) {
              answer += data.text
              if (!started) {
                started = true;
                
                setMessages((prev) => [
                  ...prev,
                  { id: botMsgId, role: 'bot', content: data.text, streaming: true },
                ])
              } else {
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === botMsgId ? { ...m, content: m.content + data.text } : m,
                  ),
                )       
              }             
            } else if (eventType === 'done') {
              if (data.sessionId) {
                localStorage.setItem('chatbot_session_id', data.sessionId)    
              }
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === botMsgId
                    ? {
                        ...m,
                        streaming: false,
                        // 가드레일/에러 메시지에는 만족도 평가를 붙이지 않는다
                        hstryTsid: m.error ? undefined : botMsgId,
                        citations: data.citations?.length ? data.citations : undefined,
                      }
                    : m,
                ),
              )

            } else if (eventType === 'error') {
              const errText = data.text || '오류가 발생했습니다.';              
              if (!started) {
                setMessages((prev) => [
                  ...prev,
                  { id: botMsgId, role: 'bot', content: errText, error: true },
                ]);
              } else {
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === botMsgId
                      ? { ...m, content: errText, streaming: false, error: true }
                      : m,
                  ),
                );
              }
            }
          } catch {
            // JSON 파싱 오류 무시
          }
        }
      }
      setLoading(false)
      setMessageSave(prev => {
        return {
          ...prev,
          ansCn: answer,
        }
      })
    } catch {
      
      setMessages((prev) => [
        ...prev,
        {
          id: generateUUID(),
          role: 'bot',
          content: '오류가 발생했습니다. 잠시 후 다시 시도해 주세요.',
          error: true,
        },
      ]);


    } finally {
      
      setLoading(false)
    }
  }

  return (
    <>
      <Slide direction="up" in={open} mountOnEnter unmountOnExit>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 140,
            right: 40,
            width: 360,
            height: 700,
            display: 'flex',
            flexDirection: 'column',
            zIndex: 1300,
            borderRadius: 3,
            overflow: 'hidden',
            border: '1.5px solid',
            borderColor: 'primary.main',
          }}
        >
          {/* 헤더 */}
          <Box
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              px: 2,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.dark' }}>                
                <img src={chatbotIcon32.src} alt="챗봇아이콘" />
              </Avatar>
              <Box>
                <Typography variant="subtitle2" fontWeight={600}>
                  유가보조금 도우미
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  무엇이든 물어보세요
                </Typography>
              </Box>
            </Stack>
            <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: 'white' }}>
              <IconX size={18} />
            </IconButton>
          </Box>

          {/* 추천 질문 - 대화 중에도 최상단 고정 표시 */}
          <Box
            sx={{
              px: 2,
              py: 1,
              bgcolor: 'grey.50',
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.8,
            }}
          >
            {visibleQuestions.map((q) => (
              <Chip
                key={q.rcmdtnQstnTsid}
                label={q.qstnTtl}
                size="small"
                variant="outlined"
                color="primary"
                clickable
                onClick={() => selectQstn(q.qstnTtl, q.rcmdtnQstnTsid)}
                sx={{ fontSize: '0.7rem' }}
              />
            ))}
            <Box sx={{ width: '100%', height: 5, display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 0.5, mb: 0.5 }}>
              {suggestedQuestions.length > 4 && (
                <IconButton onClick={() => setShowAll((prev) => !prev)}>
                  {showAll ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              )}
            </Box>            
          </Box>

          {/* 메시지 영역 */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              px: 2,
              py: 1.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              bgcolor: 'grey.50',
            }}
          >
            {messages.length === 0 && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  gap: 1,
                  color: 'primary.main',
                }}
              >
                <img src={chatbotIcon64.src} alt="AI챗봇안내" />
                <Typography variant="body2" textAlign="center" color="text.secondary">
                  안녕하세요!
                  <br />
                  궁금한 것을 물어보세요.
                </Typography>
              </Box>
            )}

            {messages.map((msg) => (
              <Stack
                key={msg.id}
                direction={msg.role === 'user' ? 'row-reverse' : 'row'}
                spacing={1}
                alignItems="flex-end"
              >
                {msg.role === 'bot' && (
                  <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main', flexShrink: 0 }}>
                    <img src={chatbotIcon24.src} alt="AI챗봇안내" />
                  </Avatar>
                )}
                <Box sx={{ maxWidth: '75%', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1,
                      borderRadius:
                        msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                      bgcolor: msg.role === 'user' ? 'primary.main' : 'white',
                      color: msg.role === 'user' ? 'white' : 'text.primary',
                      boxShadow: 1,
                    }}
                  >
                    {msg.role === 'bot' ? (
                      <Box sx={{ position: 'relative' }}>
                        <MarkdownMessage content={msg.content} />
                        {msg.streaming && (
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-block',
                              width: '2px',
                              height: '1em',
                              bgcolor: 'text.primary',
                              ml: '2px',
                              verticalAlign: 'text-bottom',
                              animation: 'blink 0.8s step-end infinite',
                              '@keyframes blink': {
                                '0%, 100%': { opacity: 1 },
                                '50%': { opacity: 0 },
                              },
                            }}
                          />
                        )}
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace: 'pre-wrap', wordBreak: 'break-word', 
                            '&::selection': {
                            backgroundColor: '#ffe082',
                            color: '#000',
                          },
                        }}
                      >
                        {msg.content}
                      </Typography>
                    )}
                  </Box>

                  {/* 출처 */}
                  {msg.role === 'bot' && msg.citations && msg.citations.length > 0 && (
                    <Box sx={{ px: 0.5 }}>
                      <CitationList citations={msg.citations} />
                    </Box>
                  )}

                  {/* 답변양식 배치 */}
                  {msg.role === 'bot' && answerCn && (
                    <Box
                      sx={{
                        mt: 1,
                        px: 1.5,
                        py: 1,
                        borderRadius: '16px 16px 16px 4px',
                        bgcolor: 'white',
                        color: 'text.primary',
                        boxShadow: 1,
                        alignSelf: 'flex-start',
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                      >
                        {answerCn}
                      </Typography>
                    </Box>
                  )}

                  {msg.role === 'bot' && (
                    <FeedbackPanel
                      hstryTsid={msg.hstryTsid}
                      onSubmit={sendFeedback}
                      current={msg.hstryTsid ? feedbackMap[msg.hstryTsid] : undefined}
                    />
                  )}
                </Box>
              </Stack>
            ))}

            {/* 스트리밍 시작 전 로딩 스피너 */}
            {loading && (
              <Stack direction="row" spacing={1} alignItems="flex-end">
                <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main', flexShrink: 0 }}>
                  <img src={chatbotIcon16.src} alt="AI챗봇" />
                </Avatar>
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    borderRadius: '16px 16px 16px 4px',
                    bgcolor: 'white',
                    boxShadow: 1,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <CircularProgress size={16} />
                </Box>
              </Stack>
            )}

            <div ref={bottomRef} />
          </Box>

          <Divider />

          {/* 입력 영역 */}
          <Box sx={{ p: 1.5, display: 'flex', gap: 1, alignItems: 'center', bgcolor: 'white' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="메시지를 입력하세요..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
            />
            <IconButton
              onClick={() => sendMessage()}
              disabled={!input.trim() || loading}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': { bgcolor: 'primary.dark' },
                '&.Mui-disabled': { bgcolor: 'grey.200', color: 'grey.400' },
              }}
            >
              <IconSend size={18} />
            </IconButton>
          </Box>
        </Paper>
      </Slide>

      {/* 플로팅 버튼 */}
      <Fab
        variant="circular"
        color="primary"
        sx={{
          position: 'fixed',
          bottom: 50,
          right: 40,
          zIndex: 1300,
          width: 64,
          height: 64,
          minWidth: 64,
          minHeight: 64,
          borderRadius: '50%',
        }}
        onClick={() => setOpen((prev) => !prev)}
      >
        <img src={chatbotIconButton33.src} alt="AI챗봇안내" />
      </Fab>
    </>
  );
};

export default ChatbotWidget;
